/* eslint-disable react/jsx-props-no-spreading */
/** @jsxImportSource @emotion/react */
import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import EmotionReactModal from "components/EmotionReactModal";
import useCreatePhaseRuleMutation from "hookMutation/useCreatePhaseRuleMutation";
import useUpdatePhaseRuleMutation from "hookMutation/useUpdatePhaseRuleMutation";
import useGetVariableQuery from "hookQueries/useGetVariableQuery";
import ModalHeader from "components/common/ModalHeader";
import { Controller, useForm } from "react-hook-form";
import Input from "components/common/Input";
import { yupResolver } from "@hookform/resolvers/yup";
import Select from "components/common/Select";
import { mTop40Css } from "assets/css/margins.css";
import validationSchema from "./validation";
import {
  buttonCss,
  leftContainerCss,
  modalContainerCss,
  modalContentCss,
  modalCss,
  rightContainerCss,
  sectionCss,
  tabsContentCss,
  titleCss,
} from "./index.css";

const ALLOWED_SOURCE_COLUMNS = [
  "Parent Company",
  "Brand",
  "Parent Category",
  "Category",
  "Title",
  "Region",
  "Prospect Persona",
];
const RULE_OPTIONS = [
  { label: "Contains", value: "contains" },
  {
    label: "Exact",
    value: "exact",
  },
];
export default function RuleFormModal({ isOpen, onClose, rule, campaignId }) {
  const isEditing = !!rule;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      source_variable: null,
      rule: RULE_OPTIONS.find((o) => o.value === "exact"),
      source_value: "",
      target_variable: null,
      target_value: "",
    },
    mode: "onChange",
  });
  const { data: customVariables } = useGetVariableQuery({ id: campaignId });
  const createMutation = useCreatePhaseRuleMutation();
  const updateMutation = useUpdatePhaseRuleMutation();

  const sourceVarOptions = useMemo(
    () => ALLOWED_SOURCE_COLUMNS.map((col) => ({ label: col, value: col })),
    [],
  );
  const targetVarOptions = useMemo(
    () => customVariables?.map((cv) => ({ label: cv.name, value: cv.name })) || [],
    [customVariables],
  );

  useEffect(() => {
    if (isEditing && rule) {
      reset({
        source_variable: sourceVarOptions.find((o) => o.value === rule.source_variable),
        rule: RULE_OPTIONS.find((o) => o.value === rule.rule),
        source_value: rule.source_value,
        target_variable: targetVarOptions.find((o) => o.value === rule.target_variable),
        target_value: rule.target_value,
      });
    } else {
      reset({
        source_variable: null,
        rule: RULE_OPTIONS.find((o) => o.value === "exact"),
        source_value: "",
        target_variable: null,
        target_value: "",
      });
    }
  }, [rule, isEditing, reset, sourceVarOptions, targetVarOptions]);

  const onSubmit = (formData) => {
    const ruleData = {
      ...formData,
      source_variable: formData.source_variable.value,
      rule: formData.rule.value,
      target_variable: formData.target_variable.value,
    };

    if (isEditing) {
      updateMutation.mutate({ ruleId: rule.id, body: ruleData }, { onSuccess: onClose });
    } else {
      createMutation.mutate({ campaignId, body: ruleData }, { onSuccess: onClose });
    }
  };

  return (
    <EmotionReactModal isOpen={isOpen} onRequestClose={onClose} className={modalCss}>
      <form onSubmit={handleSubmit(onSubmit)} css={[modalContainerCss]}>
        <div css={sectionCss}>
          <ModalHeader handleClose={onClose}>
            <span css={titleCss}>{isEditing ? "Edit Rule" : "Add Rule"}</span>
          </ModalHeader>
        </div>
        <div className="flex" css={[tabsContentCss, modalContentCss]}>
          <div css={leftContainerCss}>
            <Controller
              name="source_variable"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={sourceVarOptions}
                  label="Source Variable"
                  placeholder="Select Variable..."
                  variant="outlined"
                  error={errors.source_variable?.message}
                />
              )}
            />

            <Controller
              name="rule"
              control={control}
              render={({ field }) => (
                <Select {...field} options={RULE_OPTIONS} label="Rule" variant="outlined" />
              )}
            />

            <Input
              label="Source Value"
              error={errors.source_value?.message}
              {...register("source_value")}
            />
          </div>

          <div css={rightContainerCss}>
            <Controller
              name="target_variable"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={targetVarOptions}
                  label="Target Variable"
                  placeholder="Select Target..."
                  variant="outlined"
                  error={errors.target_variable?.message}
                />
              )}
            />
            <Input
              label="Target Value"
              error={errors.target_value?.message}
              {...register("target_value")}
            />
          </div>
        </div>
        <button
          type="submit"
          css={[buttonCss, mTop40Css]}
          disabled={createMutation.isLoading || updateMutation.isLoading}
        >
          Save
        </button>
      </form>
    </EmotionReactModal>
  );
}

RuleFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rule: PropTypes.shape({
    id: PropTypes.number,
    source_variable: PropTypes.string,
    rule: PropTypes.string,
    source_value: PropTypes.string,
    target_variable: PropTypes.string,
    target_value: PropTypes.string,
  }),
  campaignId: PropTypes.number.isRequired,
};
